import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../api/baseURL';

// Accent-insensitive contains. Client names here are Koné, Aïcha, N'Guessan, and
// nobody types the accent when looking someone up — the same trap that made the
// walk-in client picker look broken.
const fold = (s) => String(s || '')
  .normalize('NFD')
  .replace(/\p{Diacritic}/gu, '')
  .toLowerCase();

// Statuses the account model actually uses, in the order an admin cares about:
// things needing action first.
const STATUSES = ['pending', 'ghost', 'active', 'claimed', 'rejected'];

const STATUS_LABELS = {
  pending: 'En attente',
  ghost: 'Fantôme',
  active: 'Actif',
  claimed: 'Réclamé',
  rejected: 'Refusé',
};

const UserValidation = () => {
  const { token } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Filters for the all-users table. Applied client-side against the list that
  // is already loaded, so switching is instant and the per-status counts below
  // are real rather than a second round trip. The endpoint does accept
  // ?status=&role=, which is the right move once the list itself outgrows one
  // request — that needs pagination, which this page does not have yet.
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPending();
    fetchAll();
  }, []);

  const fetchPending = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/pending-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUsers(res.data.data || []);
    } catch (err) {
      console.error('Fetch pending error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(res.data.data || []);
    } catch (err) {
      console.error('Fetch users error:', err);
    }
  };

  // Per-row outcome of a resend: { [userId]: { ok, sms, email, message } }.
  // Kept per-row rather than in one banner because an admin works down a list
  // of ghosts and needs to see which ones actually went.
  const [resendResult, setResendResult] = useState({});

  // Send a ghost client a fresh activation link.
  //
  // The endpoint regenerates the token only if it has really expired, so a link
  // the client already has open keeps working, and extends the window to 30
  // days. Until now nothing in the app called it — an expired link was a dead
  // end for the client and there was no button anywhere to rescue it.
  const handleResendActivation = async (userId) => {
    setActionLoading(userId);
    setResendResult((r) => ({ ...r, [userId]: undefined }));
    try {
      const res = await axios.post(`${API_URL}/auth/resend-activation/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // The API reports the two channels separately, and "sent" now means the
      // provider accepted it — so a client with no email and a failed SMS shows
      // as a failure rather than a silent success.
      const sms = !!res.data?.sent?.sms;
      const email = !!res.data?.sent?.email;
      setResendResult((r) => ({
        ...r,
        [userId]: {
          ok: sms || email,
          sms,
          email,
          message: sms || email
            ? `Envoyé${sms ? ' par SMS' : ''}${sms && email ? ' et' : ''}${email ? ' par email' : ''}`
            : 'Aucun envoi réussi — vérifiez le téléphone et l’email du client',
        },
      }));
      fetchAll();
    } catch (err) {
      setResendResult((r) => ({
        ...r,
        [userId]: { ok: false, message: err.response?.data?.message || 'Échec de l’envoi' },
      }));
    } finally {
      setActionLoading(null);
    }
  };

  const handleValidate = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.patch(`${API_URL}/admin/users/${userId}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPending();
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur validation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId) => {
    const reason = prompt('Raison du refus (optionnel):');
    if (reason === null) return; // cancelled
    setActionLoading(userId);
    try {
      await axios.patch(`${API_URL}/admin/users/${userId}/reject`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPending();
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur refus');
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      'Car Workshop': 'bg-blue-500/20 text-blue-400',
      'Spare Parts Supplier': 'bg-orange-500/20 text-orange-400',
      'Insurance Company': 'bg-purple-500/20 text-purple-400',
      'Car Owner': 'bg-green-500/20 text-green-400',
      'Vehicle Loader': 'bg-cyan-500/20 text-cyan-400',
      'Admin': 'bg-red-500/20 text-red-400',
    };
    return colors[role] || 'bg-slate-500/20 text-slate-400';
  };

  // How many users sit in each status, from the full list — so the chip counts
  // do not change as the other filters narrow things down.
  const statusCounts = useMemo(() => {
    const counts = {};
    for (const u of allUsers) {
      const s = u.accountStatus || 'unknown';
      counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [allUsers]);

  const roles = useMemo(
    () => [...new Set(allUsers.map((u) => u.role).filter(Boolean))].sort(),
    [allUsers]);

  const visibleUsers = useMemo(() => {
    const q = fold(search.trim());
    return allUsers.filter((u) => {
      if (statusFilter !== 'all' && u.accountStatus !== statusFilter) return false;
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (!q) return true;
      // Phone is included because a client who rings up gives their number, not
      // the spelling of their name.
      return [u.name, u.email, u.businessName, u.phoneNumber]
        .some((field) => fold(field).includes(q));
    });
  }, [allUsers, statusFilter, roleFilter, search]);

  const filtersActive = statusFilter !== 'all' || roleFilter !== 'all' || search.trim() !== '';

  const getStatusBadge = (status) => {
    const map = {
      active: 'bg-green-500/20 text-green-400',
      pending: 'bg-amber-500/20 text-amber-400',
      rejected: 'bg-red-500/20 text-red-400',
      ghost: 'bg-slate-500/20 text-slate-400',
      claimed: 'bg-blue-500/20 text-blue-400',
    };
    return map[status] || 'bg-slate-500/20 text-slate-400';
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
        Validation des Comptes
      </h2>

      {/* Pending count badge */}
      {pendingUsers.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
            <span className="text-amber-400 font-bold text-sm">{pendingUsers.length}</span>
          </div>
          <span className="text-amber-300 font-medium">compte{pendingUsers.length > 1 ? 's' : ''} en attente de validation</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800/50 p-1 rounded-xl border border-slate-700/50">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'pending' ? 'bg-amber-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          En attente ({pendingUsers.length})
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'all' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          Tous les utilisateurs ({allUsers.length})
        </button>
      </div>

      {/* Pending Tab */}
      {activeTab === 'pending' && (
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Chargement...</div>
          ) : pendingUsers.length === 0 ? (
            <div className="text-center py-12 text-slate-500">Aucun compte en attente de validation</div>
          ) : (
            pendingUsers.map(user => (
              <div key={user._id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className="text-white font-semibold text-lg">{user.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadge(user.role)}`}>
                        {user.role}
                      </span>
                    </div>
                    {user.businessName && (
                      <div className="text-slate-300 text-sm">{user.businessName}</div>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                      {user.email && <span>{user.email}</span>}
                      {user.phoneNumber && <span>{user.phoneNumber}</span>}
                      <span>Type: {user.accountType}</span>
                      <span>Inscrit: {new Date(user.createdAt).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleValidate(user._id)}
                      disabled={actionLoading === user._id}
                      className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      {actionLoading === user._id ? '...' : 'Valider'}
                    </button>
                    <button
                      onClick={() => handleReject(user._id)}
                      disabled={actionLoading === user._id}
                      className="px-4 py-2 bg-red-600/80 hover:bg-red-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Refuser
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* All Users Tab */}
      {activeTab === 'all' && (
        <>
        {/* Filters. The table lists every account on the platform, newest
            first, and the thing an admin usually wants is one person or one
            status — most often the ghosts, to resend an activation link. */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                statusFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60'
              }`}
            >
              Tous ({allUsers.length})
            </button>
            {/* Only statuses that actually occur, so the row does not carry
                four chips reading (0) on a young platform. */}
            {STATUSES.filter((s) => statusCounts[s]).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  statusFilter === s
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60'
                }`}
              >
                {STATUS_LABELS[s] || s} ({statusCounts[s]})
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, email, téléphone…"
              className="flex-1 min-w-[220px] px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50
                         text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50
                         text-sm text-white focus:outline-none focus:border-blue-500"
            >
              <option value="all">Tous les rôles</option>
              {roles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            {filtersActive && (
              <button
                onClick={() => { setStatusFilter('all'); setRoleFilter('all'); setSearch(''); }}
                className="px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white
                           border border-slate-700/50 hover:border-slate-600 transition-colors"
              >
                Réinitialiser
              </button>
            )}
          </div>

          {/* Stated explicitly: a filtered table that looks like the whole list
              is how someone concludes a user does not exist. */}
          {filtersActive && (
            <p className="text-xs text-slate-500">
              {visibleUsers.length} sur {allUsers.length} utilisateur{allUsers.length > 1 ? 's' : ''}
            </p>
          )}
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-700/50">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-900/80 text-slate-400 uppercase tracking-wider text-xs border-b border-slate-700">
              <tr>
                <th className="px-4 py-3">Nom</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {visibleUsers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    {allUsers.length === 0
                      ? 'Aucun utilisateur'
                      : 'Aucun utilisateur ne correspond à ces filtres'}
                  </td>
                </tr>
              )}
              {visibleUsers.map(user => (
                <tr key={user._id} className="hover:bg-slate-800/40 transition-colors">
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{user.name}</div>
                    {user.businessName && <div className="text-xs text-slate-500">{user.businessName}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-400">{user.email || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadge(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(user.accountStatus)}`}>
                      {user.accountStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(user.createdAt).toLocaleDateString('fr-FR')}</td>
                  <td className="px-4 py-3">
                    {user.accountStatus === 'pending' && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleValidate(user._id)}
                          className="px-2 py-1 bg-green-600/80 hover:bg-green-500 text-white text-xs rounded transition-colors"
                        >
                          Valider
                        </button>
                        <button
                          onClick={() => handleReject(user._id)}
                          className="px-2 py-1 bg-red-600/80 hover:bg-red-500 text-white text-xs rounded transition-colors"
                        >
                          Refuser
                        </button>
                      </div>
                    )}

                    {/* A ghost is a walk-in client registered by a workshop who
                        has never set a password. Their activation link lasts 7
                        days; this is the only way to give them another. */}
                    {user.accountStatus === 'ghost' && (
                      <div className="flex flex-col items-start gap-1">
                        <button
                          onClick={() => handleResendActivation(user._id)}
                          disabled={actionLoading === user._id}
                          title="Renvoyer le lien d'activation par SMS et email"
                          className="px-2 py-1 bg-cyan-600/80 hover:bg-cyan-500 disabled:opacity-50
                                     text-white text-xs rounded transition-colors whitespace-nowrap"
                        >
                          {actionLoading === user._id ? 'Envoi…' : 'Renvoyer le lien'}
                        </button>
                        {resendResult[user._id] && (
                          <span className={`text-[11px] ${
                            resendResult[user._id].ok ? 'text-emerald-400' : 'text-red-400'}`}>
                            {resendResult[user._id].message}
                          </span>
                        )}
                        {!user.phoneNumber && !user.email && (
                          <span className="text-[11px] text-amber-400">
                            Ni téléphone ni email — impossible à joindre
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
};

export default UserValidation;
